/**
 * useChunkedUploader — Resumable multi-file uploads for the Patient Files
 * module. Each file becomes a "job" with independent progress, pause / resume /
 * cancel controls, and retry-with-backoff on network failures.
 *
 * Behaviour matrix:
 *   - files < 5 MB  → single-shot `POST /patients/{pid}/files` (fast path)
 *   - files ≥ 5 MB  → 3-step chunked flow (init / chunk×N / complete)
 *
 * Resume-across-refresh: the client persists in-flight `upload_id` + file
 * metadata to localStorage keyed by patientId. On mount, we probe the server
 * for each stored upload; if the session is still `in_progress` and we have
 * the same File object (from a re-drop) we can pick up where we left off.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";

const CHUNK_THRESHOLD = 5 * 1024 * 1024;
const CHUNK_SIZE = 5 * 1024 * 1024;
const MAX_RETRIES = 4;
const BACKOFF_MS = 800; // exponential: 800, 1600, 3200, 6400

let JOB_SEQ = 0;
const nextJobId = () => `job-${Date.now()}-${++JOB_SEQ}`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));


/** Individual upload job state — safe to render directly. */
const emptyJob = (file, kind) => ({
  id: nextJobId(),
  file,
  kind,
  size: file.size,
  filename: file.name,
  status: "queued",       // queued | uploading | paused | completed | error | aborted
  progress: 0,            // 0..1
  uploaded_bytes: 0,
  chunk_size: CHUNK_SIZE,
  total_chunks: 1,
  received_chunks: [],
  upload_id: null,        // set once init() succeeds
  error: null,
  started_at: null,
});


export function useChunkedUploader({ patientId, onCompleted }) {
  const [jobs, setJobs] = useState([]);
  const jobsRef = useRef([]);
  // Keep ref in sync so mutations from async functions see the latest state.
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  const updateJob = useCallback((id, patch) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  const removeJob = useCallback((id) => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  const getJob = (id) => jobsRef.current.find((j) => j.id === id);

  /* -------- single-shot upload for small files -------- */
  const runSingleShot = async (job) => {
    updateJob(job.id, { status: "uploading", started_at: Date.now() });
    const fd = new FormData();
    fd.append("file", job.file);
    try {
      const { data } = await api.post(
        `/patients/${patientId}/files?kind=${job.kind}`, fd,
        { headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = e.total ? e.loaded / e.total : 0;
            updateJob(job.id, { progress: pct, uploaded_bytes: e.loaded });
          },
        },
      );
      updateJob(job.id, { status: "completed", progress: 1, uploaded_bytes: job.size });
      onCompleted?.(data);
    } catch (err) {
      updateJob(job.id, { status: "error", error: apiError(err, "Upload failed") });
    }
  };

  /* -------- chunked flow -------- */
  const runChunked = async (job) => {
    updateJob(job.id, { status: "uploading", started_at: Date.now() });

    // 1. Init (or reuse the persisted upload_id)
    let uploadId = job.upload_id;
    let chunkSize = job.chunk_size;
    let totalChunks = job.total_chunks;
    let receivedChunks = new Set(job.received_chunks);

    if (!uploadId) {
      try {
        const { data } = await api.post(`/patients/${patientId}/uploads/init`, {
          filename: job.filename,
          total_size: job.size,
          kind: job.kind,
        });
        uploadId = data.upload_id;
        chunkSize = data.chunk_size;
        totalChunks = data.total_chunks;
        receivedChunks = new Set(data.received_chunks || []);
        updateJob(job.id, { upload_id: uploadId, chunk_size: chunkSize, total_chunks: totalChunks });
        // Persist for cross-refresh resume.
        persist(patientId, jobsRef.current);
      } catch (err) {
        updateJob(job.id, { status: "error", error: apiError(err, "Init failed") });
        return;
      }
    } else {
      // Session was resumed — refresh state from server so we don't re-upload
      // chunks the server already has.
      try {
        const { data } = await api.get(`/patients/${patientId}/uploads/${uploadId}`);
        receivedChunks = new Set(data.received_chunks || []);
      } catch { /* fall through — start over from what we have locally */ }
    }

    // Emit initial progress reflecting the already-received bytes.
    const uploadedFor = (n) => n * chunkSize; // upper bound (last chunk may be smaller)
    updateJob(job.id, {
      progress: Math.min(1, receivedChunks.size / totalChunks),
      uploaded_bytes: Math.min(job.size, uploadedFor(receivedChunks.size)),
    });

    // 2. Upload missing chunks sequentially with retry
    for (let i = 0; i < totalChunks; i++) {
      const cur = getJob(job.id);
      if (!cur || cur.status === "paused" || cur.status === "aborted") {
        return; // caller decided to stop
      }
      if (receivedChunks.has(i)) continue;

      const start = i * chunkSize;
      const end = Math.min(job.size, start + chunkSize);
      const blob = job.file.slice(start, end);
      const fd = new FormData();
      fd.append("file", blob, `${job.filename}.part${i}`);

      let attempt = 0;
      while (attempt <= MAX_RETRIES) {
        try {
          await api.post(
            `/patients/${patientId}/uploads/${uploadId}/chunk?index=${i}`, fd,
            { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
          );
          receivedChunks.add(i);
          const cur2 = getJob(job.id);
          if (!cur2) return;
          updateJob(job.id, {
            received_chunks: [...receivedChunks],
            progress: receivedChunks.size / totalChunks,
            uploaded_bytes: Math.min(job.size, uploadedFor(receivedChunks.size)),
          });
          break;
        } catch (err) {
          attempt++;
          if (attempt > MAX_RETRIES) {
            updateJob(job.id, {
              status: "error",
              error: apiError(err, `Network error on chunk ${i}`),
            });
            return;
          }
          await sleep(BACKOFF_MS * Math.pow(2, attempt - 1));
        }
      }
    }

    // 3. Complete
    try {
      const { data: rec } = await api.post(
        `/patients/${patientId}/uploads/${uploadId}/complete`,
      );
      updateJob(job.id, { status: "completed", progress: 1, uploaded_bytes: job.size });
      onCompleted?.(rec);
      clearPersisted(patientId, job.id);
    } catch (err) {
      updateJob(job.id, {
        status: "error",
        error: apiError(err, "Finalize failed"),
      });
    }
  };

  /* -------- public API -------- */
  const enqueue = useCallback((files, kind = "document") => {
    const arr = Array.from(files || []);
    const created = arr.map((f) => emptyJob(f, kind));
    setJobs((prev) => [...created, ...prev]);
    // Run in parallel — the server rate-limiter protects us.
    for (const job of created) {
      if (job.size < CHUNK_THRESHOLD) runSingleShot(job);
      else runChunked(job);
    }
  }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pause = useCallback((id) => updateJob(id, { status: "paused" }), [updateJob]);
  const resume = useCallback((id) => {
    const j = getJob(id);
    if (!j || j.status !== "paused") return;
    if (j.size < CHUNK_THRESHOLD) runSingleShot(j);
    else runChunked(j);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const abort = useCallback(async (id) => {
    const j = getJob(id);
    if (!j) return;
    updateJob(id, { status: "aborted" });
    if (j.upload_id) {
      try { await api.delete(`/patients/${patientId}/uploads/${j.upload_id}`); }
      catch { /* silent — user is trying to cancel */ }
    }
    clearPersisted(patientId, id);
  }, [patientId, updateJob]);

  const clear = useCallback((id) => removeJob(id), [removeJob]);

  const clearFinished = useCallback(() => {
    setJobs((prev) => prev.filter((j) => !["completed", "aborted"].includes(j.status)));
  }, []);

  return { jobs, enqueue, pause, resume, abort, clear, clearFinished };
}


/* -------- localStorage helpers (persist in-flight upload_ids across refresh) -------- */
const KEY = (pid) => `viyora:uploads:${pid}`;

function persist(pid, jobs) {
  try {
    const slim = jobs
      .filter((j) => j.upload_id && j.status !== "completed" && j.status !== "aborted")
      .map((j) => ({ id: j.id, upload_id: j.upload_id, filename: j.filename, size: j.size, kind: j.kind }));
    localStorage.setItem(KEY(pid), JSON.stringify(slim));
  } catch { /* ignore quota errors */ }
}

function clearPersisted(pid, jobId) {
  try {
    const raw = localStorage.getItem(KEY(pid));
    if (!raw) return;
    const arr = JSON.parse(raw).filter((j) => j.id !== jobId);
    if (arr.length) localStorage.setItem(KEY(pid), JSON.stringify(arr));
    else localStorage.removeItem(KEY(pid));
  } catch { /* ignore */ }
}
